import * as React from 'react'

import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { PopoverOrigin } from '@mui/material/Popover'

import { MoreButton as MoreButtonAtom } from '@sales-center/components'
import { useHandlers } from './hooks'
import { styles } from './useStyles'
import type { AssignmentRuleDTO } from '@user/store/leadAssignmentRule'
import { CoverageBlockDialog } from './CoverageBlockDialog'
import { DeleteAssignmentRuleDialog } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleList/DeleteAssignmentRuleDialog'

type Props = {
  assignment: AssignmentRuleDTO
  onClick: (id?: number) => void
  onView: (id?: number) => void
}

const anchorOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
}

const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
}

export function LeadAssignmentRuleItem(props: Props) {
  const { assignment, onClick, onView } = props
  const {
    handleDeleteClick,
    deleteOpened,
    openDeleteDialog,
    closeDeleteDialog,
    togglePause,
    deleteLoading,
    coverageBlockLabels,
    closeCoverageBlock,
  } = useHandlers(assignment)
  return (
    <Stack direction="row" width="100%" alignItems="center" gap="25px">
      <Box flex={1}>
        <Paper variant="outlined" elevation={0} sx={styles.cardContainer}>
          <Stack
            direction="row"
            marginTop={1.2}
            marginBottom={0.7}
            marginX={0}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              sx={{
                ...styles.dragIconContainer,
                paddingBottom: 0,
                paddingTop: assignment.description ? '0px' : '20px',
              }}
            >
              <DragIndicatorIcon
                fontSize="small"
                data-test-id="DragIndicatorIcon"
              />
            </Box>
            <Box sx={{ flex: 'auto' }} pl={0}>
              <Typography
                onClick={() => onView(assignment.id)}
                variant="h6"
                sx={{ cursor: 'pointer', ...styles.name }}
              >
                {assignment.name}
              </Typography>
              <Typography variant="body2" sx={styles.description}>
                {assignment.description}
              </Typography>
              <Typography variant="caption">
                {assignment.assignmentRuleConditions.length}
                {assignment.assignmentRuleConditions.length == 1
                  ? ' Condition '
                  : ' Conditions '}{' '}
                Created
              </Typography>
              <Box marginTop={1} marginLeft={1}>
                <FormControlLabel
                  labelPlacement="end"
                  label={
                    <Typography variant="body1" sx={styles.label}>
                      {assignment.enabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                  }
                  control={
                    <Switch
                      size="small"
                      checked={assignment.enabled}
                      onChange={togglePause}
                    />
                  }
                />
              </Box>
            </Box>
            <Box sx={styles.menuContainer}>
              <MoreButtonAtom
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                options={[
                  {
                    label: 'Edit',
                    onClick: () => onClick(assignment.id),
                    dataTestId: 'leadAssignment_edit',
                  },
                  {
                    label: 'Delete',
                    onClick: openDeleteDialog,
                    dataTestId: 'leadAssignment_delete',
                  },
                ]}
                dataTestId={`leadAssignmentMoreBtn-${assignment?.id}`}
                horizontal={false}
              />
            </Box>
          </Stack>
        </Paper>
      </Box>
      <DeleteAssignmentRuleDialog
        open={deleteOpened}
        onClose={closeDeleteDialog}
        onDelete={() => handleDeleteClick(assignment.id)}
        disabled={deleteLoading}
      ></DeleteAssignmentRuleDialog>
      <CoverageBlockDialog
        labels={coverageBlockLabels}
        onClose={closeCoverageBlock}
      />
    </Stack>
  )
}
